import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { API } from '@configs/api-endpoints';

import { LoginFailed, Logout, RefreshToken } from '@core/store/auth/auth.actions';
import { AuthState } from '@core/store/auth/auth.state';
import { SetSpinner } from '@core/store/shared/shared.actions';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private store: Store,
    public router: Router
  ) {}

  /**
   * @description - return token from storage or cookie
   */
  private getToken(): string {
    return this.store.selectSnapshot(AuthState.token);
  }

  /**
   * @description - check if current route is in control tower module
   */
  private isControlTowerRoute(): boolean {
    return this.router.url.includes('/control-tower/');
  }

  /**
   * @description - get control tower id directly from URL
   */
  private getControlTowerIdFromUrl(): string | null {
    const url = this.router.url;
    const controlTowerMatch = url.match(/\/control-tower\/([^/?]+)/);
    return controlTowerMatch ? controlTowerMatch[1] : null;
  }

  // Public pre-auth REST endpoints that issue tokens. A 401 here (e.g. wrong
  // OTP, or the guest having no JWT yet) must NOT trigger refresh -> logout ->
  // /auth/login; it should surface to the caller so it can show an inline
  // error. These bypass the auth-header + refresh cascade below.
  private readonly mfaLoginEndpoints = [
    '/users/verify-mfa-login',
    '/users/verify-control-tower-otp',
    // Ends self-signup MFA enrolment. Called over REST so the server can set
    // the refreshToken and deviceTrust cookies, and reached with no session,
    // so a 401 here must surface rather than force a logout.
    '/users/complete-mfa-setup-login',
  ];

  private readonly publicOperations = [
    'sendLoginMfaOtp',
    'SendLoginMfaOtp',
    // Self-signup email verification
    'initiateSignupVerification',
    'InitiateSignupVerification',
    'verifySignupOtp',
    'VerifySignupOtp',
    'resendSignupOtp',
    'ResendSignupOtp',
    'cancelSignupVerification',
    'CancelSignupVerification',
    'submitSignupRequest',
    'SubmitSignupRequest',
    'loadSignupResumeContext',
    'LoadSignupResumeContext',
    'resubmitSignupRequest',
    'ResubmitSignupRequest',
    'loadInvitationPrefill',
    'LoadInvitationPrefill',
    'loadOnboardingContext',
    'LoadOnboardingContext',
    'completeOnboarding',
    'CompleteOnboarding',
    // Team invitation acceptance, opened from the invitation email
    'loadTeamInvitationContext',
    'LoadTeamInvitationContext',
    'completeTeamInvitation',
    'CompleteTeamInvitation',
    'getAllCountries',
    'GetAllCountries',
    // View-only dashboard link, opened with no session
    'getSharedDashboard',
    'GetSharedDashboard',
  ];

  /**
   * @description - check if request is a public GraphQL op that must not carry a JWT
   */
  private isPublicGraphqlRequest(req: HttpRequest<unknown>): boolean {
    if (!req.url.includes('/graphql')) return false;
    const body = req.body as { query?: string; operationName?: string } | null;
    if (!body || typeof body !== 'object') return false;
    if (body.operationName && this.publicOperations.includes(body.operationName)) return true;
    return !!body.query && this.publicOperations.some(op => body.query!.includes(op));
  }

  /**
   * @description - check if request is OTP-related and should bypass authorization
   */
  private isOtpRelatedRequest(req: HttpRequest<unknown>): boolean {
    if (this.mfaLoginEndpoints.some(endpoint => req.url.includes(endpoint))) {
      return true;
    }

    // Check if it's a GraphQL request
    if (req.url.includes('/graphql')) {
      const body = req.body;
      if (body && typeof body === 'object') {
        const graphqlBody = body as { query?: string; operationName?: string };

        const otpOperations = [
          'getAccessLinkByToken',
          'GetAccessLinkByToken',
          'initiateOtpVerification',
          'InitiateOtpVerification',
          'verifyOtp',
          'VerifyOtp',
          'verifyControlTowerOtp',
          'VerifyControlTowerOtp',
          'resendOtp',
          'ResendOtp',
          'validateAccountActivationToken',
          'ValidateToken',
          'setPasswordWithActivationToken',
          'SetPassword',
          'initMfaSetup',
          'InitMfaSetup',
          'verifyAndEnableMfa',
          'VerifyAndEnableMfa',
          'completeAccountActivation',
          'CompleteActivation',
          'resendAccountActivationLink',
          'ResendActivation',
          'initEmailMfaSetup',
          'InitEmailMfaSetup',
          'initSmsMfaSetup',
          'InitSmsMfaSetup',
          'verifyEmailMfaCode',
          'VerifyEmailMfaCode',
          'verifySmsMfaCode',
          'VerifySmsMfaCode',
          'resendMfaOtp',
          'ResendMfaOtp',
          'completeMfaSetupLogin',
          'CompleteMfaSetupLogin',
        ];

        // Check operationName first (if present)
        if (graphqlBody.operationName && otpOperations.includes(graphqlBody.operationName)) {
          return true;
        }

        // Check query string for OTP operations
        if (graphqlBody.query) {
          return otpOperations.some(
            operation =>
              graphqlBody.query!.includes(operation) ||
              graphqlBody.query!.includes(`mutation ${operation}`) ||
              graphqlBody.query!.includes(`query ${operation}`)
          );
        }
      }
    }
    return false;
  }

  /**
   * @description - intercept the http request and put jwt token
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.getToken();

    if (this.isPublicGraphqlRequest(req)) {
      return next.handle(req);
    }

    let clonedRequest = req;
    if (this.isOtpRelatedRequest(req)) {
      clonedRequest = this.addRequestHeader(req, token);
      return next.handle(clonedRequest);
    }
    if (
      !req.url.includes('storage.googleapis.com') &&
      !req.url.includes('script.google.com') &&
      !this.isOtpRelatedRequest(req)
    ) {
      clonedRequest = req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });

      // Add control tower id header if we're in control tower module
      if (this.isControlTowerRoute()) {
        const controlTowerId = this.getControlTowerIdFromUrl();
        if (controlTowerId) {
          clonedRequest = clonedRequest.clone({
            setHeaders: {
              ...clonedRequest.headers.keys().reduce(
                (headers, key) => {
                  headers[key] = clonedRequest.headers.get(key) || '';
                  return headers;
                },
                {} as { [key: string]: string }
              ),
              'x-control-tower-id': controlTowerId,
            },
          });
        }
      }
    }
    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.url.includes(API.auth.getToken)) {
          this.store.dispatch(new LoginFailed(error.message));
          return throwError(error);
        }
        if (error.status === HttpStatusCode.Unauthorized) {
          if (req.url.includes(API.auth.refreshToken) || req.url.includes(API.auth.logout)) {
            return throwError(() => error);
          }

          // On public /auth pages (login, invite activation, MFA verify, password
          // reset) a stray 401 from a background/bootstrap call must NOT trigger
          // refresh -> logout -> redirect. Doing so bounced invitees off the
          // activation screen. There is no legitimate case where a 401 on an
          // /auth page should force a logout (the user is already unauthenticated
          // on a public page), so let the request fail quietly instead.
          if (this.router.url.startsWith('/auth')) {
            return throwError(() => error);
          }

          return this.handleRefreshToken(req, next);
        }
        this.store.dispatch(new SetSpinner(false));
        return throwError(error);
      })
    );
  }

  /**
   * @description - refresh token
   */
  private handleRefreshToken(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.store.dispatch(new RefreshToken()).pipe(
        switchMap(() => {
          this.isRefreshing = true;
          const token = this.getToken();
          if (token) {
            this.refreshTokenSubject.next(token);
            return next.handle(this.addRequestHeader(req, token));
          } else {
            this.store.dispatch(new Logout(true, this.router.url));
            return throwError(() => new Error('No token found'));
          }
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.store.dispatch(new Logout(true, this.router.url));
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
          this.store.dispatch(new SetSpinner(false));
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token: string | null) => token !== null),
        take(1),
        switchMap(token => {
          return next.handle(this.addRequestHeader(req, token as string));
        })
      );
    }
  }

  /**
   * @description - add new refreshed headers to the request
   */
  private addRequestHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    let clonedRequest = req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });

    // Add control tower id header if we're in control tower module
    if (this.isControlTowerRoute()) {
      const controlTowerId = this.getControlTowerIdFromUrl();
      if (controlTowerId) {
        clonedRequest = clonedRequest.clone({
          setHeaders: {
            ...clonedRequest.headers.keys().reduce(
              (headers, key) => {
                headers[key] = clonedRequest.headers.get(key) || '';
                return headers;
              },
              {} as { [key: string]: string }
            ),
            'x-control-tower-id': controlTowerId,
          },
        });
      }
    }

    return clonedRequest;
  }
}
